import { computed } from 'vue'
import { useUserProfileStore } from '@/stores/userProfile'
import type { PermissionValue } from '@/utils/permissionBitmask'

/**
 * Composable for checking user permissions in components.
 *
 * Usage:
 * ```vue
 * <script setup>
 * import { usePermissions } from '@/composables/usePermissions'
 * import { Permission } from '@/utils/permissionBitmask'
 *
 * const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions()
 * </script>
 *
 * <template>
 *   <button v-if="hasPermission(Permission.CLIENTS_CREATE)">Create Client</button>
 * </template>
 * ```
 */
export function usePermissions() {
  const userProfileStore = useUserProfileStore()

  const permissions = computed(() => userProfileStore.permissions)

  /**
   * Check if the current user has a specific permission.
   */
  function hasPermission(permission: PermissionValue): boolean {
    return permissions.value.includes(permission)
  }

  /**
   * Check if the current user has any of the specified permissions.
   */
  function hasAnyPermission(...requiredPermissions: PermissionValue[]): boolean {
    return requiredPermissions.some((p) => permissions.value.includes(p))
  }

  /**
   * Check if the current user has all of the specified permissions.
   */
  function hasAllPermissions(...requiredPermissions: PermissionValue[]): boolean {
    return requiredPermissions.every((p) => permissions.value.includes(p))
  }

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  }
}
